import ZTable, { IColumnType, IFilterItem, ITableRef } from '@/components/ZTable';
import { ModalWidth, VALIDATE_REQUIRED } from '@/config';
import { Button, Col, Form, Input, message, Modal, Row, Space, Tooltip } from 'antd';
import React from 'react';
import { PlusCircleOutlined, SaveOutlined, LinkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import CountryInput from '@/components/FormItems/CountryInput';
import RegionInput from '@/components/FormItems/RegionInput';
import CityInput from '@/components/FormItems/CityInput';
import PartnerConnectivityDetailsModal from './PartnerConnectivityDetailsModal';
import ExportColumnsSelectorModal from '@/components/modals/ExportColumnsSelectorModal';
import { exportService } from '@/services/exportService';
import ExportBtn from '@/components/app/ExportBtn';
import SingleImageUpload from '@/components/FormItems/SingleImageUpload';
import { BlobStorageContainerType } from '@/constants';
import { clearAllPaymentPartnersCache } from '@/hooks/data/masterDataHook';
import { PartnerAccountDto } from '@/__generated';
import { apiSdk } from '@/httpClient';

type Props = {
  partnerType: number;
};

const filters: Array<IFilterItem> = [{ type: 'TEXT', name: 'searchText', label: 'partnerPage_SearchText', xlSize: 8, xxlSize: 8 }];

export default function PartnerAccount({ partnerType }: Props) {
  const zTb = React.useRef<ITableRef>();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [formVisible, setFormVisible] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string>();
  const [paymentPartnerId, setPaymentPartnerId] = React.useState<string>();
  const [exportColumns, setExportColumns] = React.useState<string[]>([]);
  const [showExportModal, toggleExportModal] = React.useState<boolean>(false);

  const clearCache = () => {
    clearAllPaymentPartnersCache();
  };

  React.useEffect(() => {
    apiSdk.PaymentPartnerService.getExportColumns().then(res => {
      setExportColumns(res);
    });
  }, []);
  const onExportExcel = (cols: string[]) => {
    exportService
      .exportPaymentPartner({
        searchText: zTb.current?.getFilter()?.searchText,
        columns: cols,
        partnerType: partnerType as any,
      })
      .toExcel();
  };
  const onExportPdf = (cols: string[]) => {
    exportService
      .exportPaymentPartner({
        searchText: zTb.current?.getFilter()?.searchText,
        columns: cols,
        partnerType: partnerType as any,
      })
      .toPdf();
  };
  const createNewPartner = (values: any) => {
    setLoading(true);
    apiSdk.PaymentPartnerService.create({ body: { ...values, partnerType: partnerType } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          clearCache();
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updatePartner = (values: any) => {
    setLoading(true);
    apiSdk.PaymentPartnerService.update({ body: { ...values, partnerType: partnerType } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          setEditingId(undefined);
          clearCache();
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onEditPartner = () => {
    setLoading(true);
    apiSdk.PaymentPartnerService.getDetail({ id: editingId || '' })
      .then(res => {
        if (res) {
          form.setFieldsValue({ ...res });
          setFormVisible(true);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  React.useEffect(() => {
    if (editingId) {
      onEditPartner();
    }
  }, [editingId]);

  const columns: IColumnType<PartnerAccountDto>[] = React.useMemo(() => {
    return [
      {
        title: 'partnerPage_Name',
        dataIndex: 'partnerName',
        sorter: true,
        defaultSortOrder: 'ascend',
      },
      {
        title: 'base.PhoneNumber',
        dataIndex: 'phoneNumber',
        ellipsis: true,
      },
      {
        title: 'partnerPage_TechnicalPhoneNumber',
        dataIndex: 'technicalPhoneNumber',
        ellipsis: true,
      },
      {
        title: 'partnerPage_ContactEmail',
        dataIndex: 'contactEmail',
        ellipsis: true,
      },
      {
        title: 'partnerPage_TechnicalSupportEmail',
        dataIndex: 'technicalSupportEmail',
        ellipsis: true,
      },
      {
        title: 'base.Address',
        dataIndex: 'address',
        ellipsis: true,
      },
    ] as IColumnType<PartnerAccountDto>[];
  }, []);
  return (
    <>
      <ZTable
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        url="/commons/payment-partners/get-payment-partners"
        bordered
        defaultFilter={{
          partnerType: partnerType,
        }}
        filters={filters}
        order_by="partnerName"
        order_by_asc={false}
        allowEdit={true}
        allowDelete={true}
        onEdit={record => {
          setEditingId(record.id);
        }}
        delete_url="/commons/payment-partners/delete"
        searchBtnAlignLeft
        extraActions={[
          {
            render: record => {
              return (
                <Tooltip title={t('partnerPage_ConnectivityDetails')}>
                  <Button
                    style={{ borderRadius: 0 }}
                    type="primary"
                    size="small"
                    icon={<LinkOutlined />}
                    onClick={() => {
                      setPaymentPartnerId(record.id);
                    }}
                  ></Button>
                </Tooltip>
              );
            },
          },
        ]}
        extraBtns={
          <Space>
            <Button
              key="create"
              type="primary"
              className="nta-create-btn"
              icon={<PlusCircleOutlined />}
              onClick={() => {
                form.resetFields();
                setFormVisible(true);
              }}
            >
              {t('createNew')}
            </Button>
            <ExportBtn
              disabled={!exportColumns?.length}
              onClick={() => {
                toggleExportModal(true);
              }}
            />
          </Space>
        }
        onDeleteCompleted={clearCache}
      />
      {formVisible ? (
        <Modal
          style={{ top: 10 }}
          visible={true}
          maskClosable={false}
          width={ModalWidth.small}
          className="nta-custom-form"
          title={!editingId ? t('partnerPage_CreatePartner') : t('partnerPage_UpdatePartner')}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
            setEditingId(undefined);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updatePartner(values);
                } else {
                  createNewPartner(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 12, xl: 12 }}>
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Row gutter={6}>
              <Col md={24}>
                <Form.Item label={t('partnerPage_Name')} name="partnerName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>

                <Form.Item label={t('base.Country')} name="countryId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <CountryInput />
                </Form.Item>

                <Form.Item label={t('base.Region')} name="regionId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <RegionInput />
                </Form.Item>

                <Form.Item label={t('base.City')} name="cityId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <CityInput />
                </Form.Item>

                <Form.Item label={t('base.Address')} name="address">
                  <Input.TextArea />
                </Form.Item>

                <Form.Item label={t('base.PhoneNumber')} name="phoneNumber" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>

                <Form.Item label={t('partnerPage_TechnicalPhoneNumber')} name="technicalPhoneNumber">
                  <Input />
                </Form.Item>
                <Form.Item label={t('partnerPage_ContactEmail')} name="contactEmail">
                  <Input />
                </Form.Item>

                <Form.Item label={t('partnerPage_TechnicalSupportEmail')} name="technicalSupportEmail">
                  <Input />
                </Form.Item>

                <Form.Item label={t('partnerPage_MonetaryZone')} name="monetaryZone">
                  <Input />
                </Form.Item>
                <Form.Item label={t('partnerPage_LogoImage')} name="logoImageUrl">
                  <SingleImageUpload blobStorageContainer={BlobStorageContainerType.CompaniesContainer} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      ) : null}
      {paymentPartnerId ? (
        <PartnerConnectivityDetailsModal paymentPartnerId={paymentPartnerId} onClose={() => setPaymentPartnerId(undefined)} />
      ) : null}
      {showExportModal ? (
        <ExportColumnsSelectorModal
          title={t('passengerPage_ExportPaymentPartner')}
          onClose={() => toggleExportModal(false)}
          columns={exportColumns}
          onExportExcel={onExportExcel}
          onExportPdf={onExportPdf}
        />
      ) : null}
    </>
  );
}
